<template>
  <div class="feed">
    <ftu-block
      dismiss-key="feed"
      icon="rss_feed"
      title="This is the Feed"
      description="The Feed is a curated content hub of sports card collecting content. We include content from some of best content creators in the hobby, with hand-picked podcasts, videos, articles, and headlines that we believe are worthy of consuming. You'll also find helpful updates from and about Card Ladder here."
    />

    <page-header title="Feed">
      <template #actions>
        <icon-button
          v-if="isStaff"
          icon="add_circle"
          @click="addPost"
        />
      </template>
    </page-header>

    <align class="header" flex direction="flex-start">
      <div>
        <search-area
          v-model="query"
          class="input"
        />
      </div>

      <pill-dropdown
        v-model="filter"
        :options="filterOptions"
      />
    </align>

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="search_off"
        title="There are no results for your query"
        description="Try searching for something else"
      />

      <template>
        <div class="list">
          <post-item
            v-for="(post, i) in posts"
            :key="post.id"
            :post="post"
            :rank="i + 1"
            :is-admin="isAdmin"
            @deletePost="deletePost"
          />
        </div>

        <infinite-scroll
          :loading="loading"
          :has-more-data="hasMoreData"
          @load="nextPage"
        />
      </template>
    </div>

    <scroll-top />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as Sentry from '@sentry/vue';
import debounce from 'lodash.debounce';

import { POST_CATEGORIES } from '@cardladder/shared/constants';

import PostItem from '@cardladder/shared/components/PostItem';

import { searchService, feedService } from '@/services';

export default {
  components: {
    PostItem,
  },

  data() {
    return {
      query: '',
      limit: 20,
      page: 1,
      posts: [],
      loaded: false,
      hasMoreData: true,
      loading: false,
      filterOptions: [
        { value: 'all', label: 'All', tooltip: 'Show all feed content' },
        ...POST_CATEGORIES,
      ],
    };
  },

  created() {
    this.reset = debounce(this.reset, 500);

    if (this.$route.query.q) {
      this.query = this.$route.query.q;
    }

    if (this.$route.query.f) {
      this.filter = this.$route.query.f;
    }

    this.search();
  },

  mounted() {
    this.$bus.$on('posts-changed', this.reset);
  },

  beforeDestroy() {
    this.$bus.$off('posts-changed', this.reset);
  },

  watch: {
    filter() {
      this.reset();
    },

    query() {
      this.reset();
    },

    page(value) {
      if (value > 1) {
        this.search();
      }
    },

    '$route.query.q': {
      handler(value) {
        this.query = value;
      },
    },
  },

  computed: {
    ...mapGetters({
      isStaff: 'isStaff',
      isAdmin: 'isAdmin',
    }),

    filter: {
      get() {
        const activeFilter = this.filterOptions.find(({ value }) => value === this.$store.state.feedFilter);

        if (activeFilter) {
          return this.$store.state.feedFilter;
        }

        return this.filterOptions[0].value;
      },

      set(value) {
        this.$store.commit('SET_FEED_FILTER', value);
      },
    },

    showEmptyState() {
      return !this.loading && this.loaded && this.posts.length === 0;
    },
  },

  methods: {
    async search(reset = false) {
      try {
        this.loading = true;

        const filters = {};

        if (this.filter !== 'all') {
          filters.category = this.filter;
        }

        const data = await searchService.search({
          index: 'feed',
          query: this.query,
          page: this.page,
          limit: this.limit,
          filters,
        });

        const posts = data.hits;

        if (reset) {
          this.posts = posts;
        } else {
          this.posts = [...this.posts, ...posts];
        }

        if (posts.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        this.$toasted.error('Error loading the feed');
        Sentry.captureException(e);
      }

      this.loaded = true;
      this.loading = false;
    },

    nextPage() {
      this.page += 1;
    },

    reset() {
      this.loaded = false;
      this.page = 1;
      this.posts = [];
      this.hasMoreData = true;
      this.search(true);
    },

    async deletePost(id) {
      if (window.confirm('Are you sure you want to delete this post?')) {
        try {
          await feedService.deletePost(id);
          this.$toasted.success('Deleted the post!');
        } catch (e) {
          this.$toasted.error('Error deleting the post');
          Sentry.captureException(e);
        }
      }
    },

    addPost() {
      this.$bus.$emit('admin-edit-post');
    },
  },
};
</script>

<style lang="scss" scoped>
.feed {
  @include container;
  min-height: 100%;
}

.header {
  margin-bottom: $gp;
}

.results {
  position: relative;
  flex: 1;
}

.list {
  @media($medium-down) {
    margin: 0 (-$gp);
  }
}
</style>
